import { getWebsiteUrl } from '../config'
import { copyrightLine } from '../../../website/src/copyright.js'
import { withBrand } from '../Brand'
import { FOOTER_DISCLAIMER, FOOTER_SUBLINE, FOOTER_TAGLINE } from '../trustCopy'

/**
 * The site footer, same content and layout as the website's
 * templates/footer.html and the dashboard's SiteFooter: brand + tagline,
 * Product / Company / Legal columns, disclaimer. Every website page is an
 * absolute link off the configured website origin. Labels and order are
 * checked against the template by SiteFooter.test.tsx.
 */
const external = { target: '_blank', rel: 'noopener noreferrer' } as const
const linkCls = 'transition-colors hover:text-primary'
const headingCls = 'mb-3 font-mono text-xs uppercase tracking-wider text-gray-400'
const listCls = 'space-y-2 text-sm text-gray-300'

export function SiteFooter() {
  const websiteUrl = getWebsiteUrl()
  const site = (path: string) => `${websiteUrl}${path}`

  return (
    <footer data-testid="site-footer" className="border-t border-white/10">
      <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5 md:pr-8">
            <a href={websiteUrl} {...external} className="inline-flex items-center gap-2">
              <img
                src={`${websiteUrl}/logo-transparent.png`}
                alt="ComplyEdge"
                className="h-6 w-auto"
              />
              <span className="font-display font-bold text-primary">ComplyEdge</span>
            </a>
            <p className="mt-4 text-base text-gray-200">{FOOTER_TAGLINE}</p>
            <p className="mt-2 text-sm leading-relaxed text-gray-400">{FOOTER_SUBLINE}</p>
          </div>
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 md:col-span-7">
            <div>
              <p className={headingCls}>Product</p>
              <ul className={listCls}>
                <li>
                  <a href={site('/rulebook/')} {...external} className={linkCls}>
                    Rulebook
                  </a>
                </li>
                <li>
                  <a href={site('/for-auditors/')} {...external} className={linkCls}>
                    For Auditors
                  </a>
                </li>
                <li>
                  <a href={site('/docs/')} {...external} className={linkCls}>
                    Docs
                  </a>
                </li>
                <li>
                  <a href={site('/docs/quick-start.html')} {...external} className={linkCls}>
                    Quick Start
                  </a>
                </li>
                <li>
                  <a href={site('/docs/api-reference.html')} {...external} className={linkCls}>
                    API Reference
                  </a>
                </li>
                <li>
                  <a
                    href={site('/docs/audit-reconciliation.html')}
                    {...external}
                    className={linkCls}
                  >
                    Audit reconciliation
                  </a>
                </li>
                <li>
                  <a href={site('/docs/playground.html')} {...external} className={linkCls}>
                    Playground
                  </a>
                </li>
                <li>
                  <a href={site('/docs/trust-badge.html')} {...external} className={linkCls}>
                    Enforcement Seal
                  </a>
                </li>
                <li>
                  <a href={site('/cco-art26.html')} {...external} className={linkCls}>
                    Article 26 One-Pager
                  </a>
                </li>
                <li>
                  <a href={site('/legal/dpo-pack.html')} {...external} className={linkCls}>
                    DPO pack
                  </a>
                </li>
                <li>
                  <a href={site('/#enterprise')} {...external} className={linkCls}>
                    Pricing
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <p className={headingCls}>Company</p>
              <ul className={listCls}>
                <li>
                  <a
                    href="https://github.com/ComplyEdge/complyedge"
                    {...external}
                    className={linkCls}
                  >
                    GitHub
                  </a>
                </li>
                <li>
                  <a href={site('/blog/index.html')} {...external} className={linkCls}>
                    Blog
                  </a>
                </li>
                <li>
                  {/* Human contact is for a sale only (2026-10-15). The website
                      has no Enterprise page and no demo: this lands on the
                      homepage plans, where the Enterprise card opens the form. */}
                  <a href={site('/#enterprise')} {...external} className={linkCls}>
                    Contact sales
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <p className={headingCls}>Legal</p>
              <ul className={listCls}>
                <li>
                  <a href={site('/legal/terms-of-service.html')} {...external} className={linkCls}>
                    Terms
                  </a>
                </li>
                <li>
                  <a href={site('/legal/privacy-policy.html')} {...external} className={linkCls}>
                    Privacy
                  </a>
                </li>
                <li>
                  <a href={site('/legal/refund-policy.html')} {...external} className={linkCls}>
                    Refunds
                  </a>
                </li>
                <li>
                  <a
                    href={site('/legal/data-processing-agreement.html')}
                    {...external}
                    className={linkCls}
                  >
                    DPA
                  </a>
                </li>
                <li>
                  <a href={site('/legal/saas-agreement.html')} {...external} className={linkCls}>
                    SaaS Agreement
                  </a>
                </li>
                <li>
                  {/* Reopens the analytics choice (analytics-consent.js listens
                      for this attribute), the same entry as the website footer. */}
                  <button type="button" data-ce-analytics-settings className={linkCls}>
                    Analytics settings
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <p className="mt-12 border-t border-white/10 pt-8 text-center text-sm leading-relaxed text-gray-400">
          {withBrand(FOOTER_DISCLAIMER)}
        </p>
        <p
          data-testid="site-footer-copyright"
          className="mt-3 text-center text-sm leading-relaxed text-gray-400"
        >
          {withBrand(copyrightLine())}
        </p>
      </div>
    </footer>
  )
}
